import { useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Calculator, TrendingUp, TrendingDown, Info } from "lucide-react";

/**
 * Voucher-Giveaway Break-Even Calculator.
 *
 * Answers the question the user asks aloud on the floor:
 *   "If I say 'spend R500, get a free R50 airtime voucher' — is that
 *    actually going to make me money, or am I giving the store away?"
 *
 * Math (all in Rand, all figures per-basket):
 *   basket_ex_vat  = threshold / (1 + vat_pct/100)        (if threshold incl VAT)
 *   gross_profit   = basket_ex_vat * gross_margin_pct/100
 *   net_profit     = gross_profit - voucher_cost
 *   effective_disc = voucher_value / threshold * 100        (what shopper sees)
 *   break_even_bsk = voucher_cost / (gross_margin_pct/100)  (VAT-ex basket
 *                    needed at current margin to fully cover the voucher)
 *   uplift_gp      = (threshold - baseline_basket) *
 *                       gross_margin_pct/100                (extra GP earned
 *                    per shopper pushed FROM baseline TO threshold)
 *
 * Baseline basket lets the owner model the realistic uplift the promo has
 * to drive to be worth running. If the promo doesn't move the average
 * basket up meaningfully, the store just gives away R50 airtime.
 */
const fmt = (n) => {
  const v = typeof n === "number" ? n : parseFloat(n);
  return isFinite(v)
    ? `R ${v.toLocaleString("en-ZA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : "R 0.00";
};

const fmtPct = (n) => {
  const v = typeof n === "number" ? n : parseFloat(n);
  return isFinite(v) ? `${v.toFixed(1)}%` : "0.0%";
};

const num = (v, fallback = 0) => {
  const n = parseFloat(v);
  return isFinite(n) ? n : fallback;
};

const VoucherGiveawayCalculator = () => {
  // Sensible starting defaults for Bosveld's typical hardware basket.
  const [threshold, setThreshold] = useState(500);
  const [voucherValue, setVoucherValue] = useState(50);
  const [voucherCost, setVoucherCost] = useState(48); // Flash VAS commission usually leaves ~4-5% margin
  const [grossMarginPct, setGrossMarginPct] = useState(25);
  const [vatPct, setVatPct] = useState(15);
  const [thresholdInclVat, setThresholdInclVat] = useState(true);
  const [baselineBasket, setBaselineBasket] = useState(280);

  const result = useMemo(() => {
    const t = num(threshold);
    const vVal = num(voucherValue);
    const vCost = num(voucherCost);
    const gm = num(grossMarginPct);
    const vat = num(vatPct);
    const bl = num(baselineBasket);

    const basketExVat = thresholdInclVat ? t / (1 + vat / 100) : t;
    const baselineExVat = thresholdInclVat ? bl / (1 + vat / 100) : bl;

    const grossProfit = basketExVat * (gm / 100);
    const netProfit = grossProfit - vCost;
    const netMarginPctOfBasket = t > 0 ? (netProfit / t) * 100 : 0;
    const effectiveDiscount = t > 0 ? (vVal / t) * 100 : 0;

    // Break-even basket: at THIS margin, how big must the (VAT-excl) basket
    // be for GP to exactly cover the voucher cost?
    const breakEvenBasketExVat = gm > 0 ? vCost / (gm / 100) : Infinity;
    const breakEvenBasketInclVat = isFinite(breakEvenBasketExVat)
      ? breakEvenBasketExVat * (1 + vat / 100)
      : Infinity;

    // Break-even margin: at THIS threshold, what margin do we need for GP
    // to exactly cover the voucher cost?
    const breakEvenMarginPct = basketExVat > 0 ? (vCost / basketExVat) * 100 : 0;

    // Uplift analysis: extra GP compared to the baseline basket. If the
    // promo doesn't push a shopper's basket above baseline + break-even,
    // we're subsidising a shopper who would have spent close to threshold
    // anyway.
    const uplift = t - bl;
    const upliftGp = uplift * (gm / 100);
    const netProfitVsBaseline = upliftGp - vCost;

    // Equivalent straight discount: what % off would give the shopper the
    // same rand value as this voucher, ignoring the airtime margin?
    const equivalentDiscountPct = t > 0 ? (vVal / t) * 100 : 0;

    return {
      basketExVat,
      baselineExVat,
      grossProfit,
      netProfit,
      netMarginPctOfBasket,
      effectiveDiscount,
      breakEvenBasketExVat,
      breakEvenBasketInclVat,
      breakEvenMarginPct,
      uplift,
      upliftGp,
      netProfitVsBaseline,
      equivalentDiscountPct,
    };
  }, [threshold, voucherValue, voucherCost, grossMarginPct, vatPct, thresholdInclVat, baselineBasket]);

  const verdict = (() => {
    if (result.netProfitVsBaseline > 0 && result.netProfit > 0)
      return { color: "text-emerald-700 bg-emerald-50 border-emerald-200", label: "WORTH IT", icon: TrendingUp };
    if (result.netProfit > 0)
      return { color: "text-amber-700 bg-amber-50 border-amber-200", label: "MARGINAL", icon: Info };
    return { color: "text-rose-700 bg-rose-50 border-rose-200", label: "LOSS-MAKING", icon: TrendingDown };
  })();
  const VerdictIcon = verdict.icon;

  return (
    <div className="space-y-6" data-testid="voucher-calc-root">
      <div className="flex items-start gap-3">
        <div className="rounded-lg bg-indigo-100 p-3">
          <Calculator className="h-6 w-6 text-indigo-700" />
        </div>
        <div>
          <h3 className="text-lg font-bold text-slate-900">
            Voucher-Giveaway Calculator
          </h3>
          <p className="text-sm text-slate-600 max-w-xl">
            &quot;Spend R500, get R50 free airtime.&quot; Punch in the numbers
            below to see whether the promo lifts profit or bleeds it. The
            model accounts for VAT, gross margin, the store&apos;s wholesale
            voucher cost, and how much basket-uplift the promo really needs
            to break even against your current average basket.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* ============ INPUTS ============ */}
        <Card data-testid="voucher-calc-inputs">
          <CardHeader>
            <CardTitle className="text-base">Inputs</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="calc-threshold">
                Basket spend threshold (R)
                <span className="text-xs text-slate-500 ml-1">— shopper must reach this</span>
              </Label>
              <Input
                id="calc-threshold"
                type="number"
                step="1"
                value={threshold}
                onChange={(e) => setThreshold(e.target.value)}
                data-testid="calc-threshold"
              />
              <div className="mt-1 flex items-center gap-2 text-xs text-slate-600">
                <input
                  id="calc-threshold-incl-vat"
                  type="checkbox"
                  checked={thresholdInclVat}
                  onChange={(e) => setThresholdInclVat(e.target.checked)}
                  data-testid="calc-threshold-incl-vat"
                />
                <label htmlFor="calc-threshold-incl-vat">Threshold is VAT inclusive</label>
              </div>
            </div>

            <div>
              <Label htmlFor="calc-voucher-value">
                Voucher face-value (R)
                <span className="text-xs text-slate-500 ml-1">— what the customer sees</span>
              </Label>
              <Input
                id="calc-voucher-value"
                type="number"
                step="1"
                value={voucherValue}
                onChange={(e) => setVoucherValue(e.target.value)}
                data-testid="calc-voucher-value"
              />
            </div>

            <div>
              <Label htmlFor="calc-voucher-cost">
                Your cost per voucher (R)
                <span className="text-xs text-slate-500 ml-1">— what the store pays Flash VAS</span>
              </Label>
              <Input
                id="calc-voucher-cost"
                type="number"
                step="0.01"
                value={voucherCost}
                onChange={(e) => setVoucherCost(e.target.value)}
                data-testid="calc-voucher-cost"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="calc-gm">Basket gross margin (%)</Label>
                <Input
                  id="calc-gm"
                  type="number"
                  step="0.1"
                  value={grossMarginPct}
                  onChange={(e) => setGrossMarginPct(e.target.value)}
                  data-testid="calc-gm-pct"
                />
              </div>
              <div>
                <Label htmlFor="calc-vat">VAT rate (%)</Label>
                <Input
                  id="calc-vat"
                  type="number"
                  step="0.1"
                  value={vatPct}
                  onChange={(e) => setVatPct(e.target.value)}
                  data-testid="calc-vat-pct"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="calc-baseline">
                Baseline average basket (R)
                <span className="text-xs text-slate-500 ml-1">— what most shoppers currently spend</span>
              </Label>
              <Input
                id="calc-baseline"
                type="number"
                step="1"
                value={baselineBasket}
                onChange={(e) => setBaselineBasket(e.target.value)}
                data-testid="calc-baseline-basket"
              />
              <p className="text-xs text-slate-500 mt-1">
                If this promo just pushes a R280 shopper up to R500, that&apos;s
                the uplift that must cover the voucher.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* ============ RESULTS ============ */}
        <div className="space-y-4">
          {/* Verdict banner */}
          <Alert className={`border-2 ${verdict.color}`} data-testid="calc-verdict">
            <VerdictIcon className="h-5 w-5" />
            <AlertDescription>
              <div className="flex items-center gap-2 font-bold text-base">
                {verdict.label}
              </div>
              <div className="mt-1 text-sm">
                Net profit per redeemed voucher: <strong>{fmt(result.netProfit)}</strong>
                {" · "}
                Vs. baseline shopper: <strong>{fmt(result.netProfitVsBaseline)}</strong>
              </div>
            </AlertDescription>
          </Alert>

          <Card data-testid="calc-per-basket">
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Per redeemed voucher</CardTitle>
            </CardHeader>
            <CardContent className="text-sm">
              <dl className="grid grid-cols-2 gap-x-4 gap-y-2">
                <dt className="text-slate-600">Basket (ex VAT)</dt>
                <dd className="text-right font-mono">{fmt(result.basketExVat)}</dd>

                <dt className="text-slate-600">Gross profit @ {fmtPct(grossMarginPct)} margin</dt>
                <dd className="text-right font-mono">{fmt(result.grossProfit)}</dd>

                <dt className="text-slate-600">Less voucher cost</dt>
                <dd className="text-right font-mono text-rose-700">
                  −{fmt(voucherCost)}
                </dd>

                <dt className="font-semibold text-slate-900 border-t pt-2 mt-2">Net profit</dt>
                <dd className={`text-right font-mono font-bold border-t pt-2 mt-2 ${result.netProfit >= 0 ? "text-emerald-700" : "text-rose-700"}`}>
                  {fmt(result.netProfit)}
                </dd>

                <dt className="text-slate-600">Net margin (of basket)</dt>
                <dd className={`text-right font-mono ${result.netMarginPctOfBasket >= 0 ? "text-emerald-700" : "text-rose-700"}`}>
                  {fmtPct(result.netMarginPctOfBasket)}
                </dd>

                <dt className="text-slate-600">Effective discount to shopper</dt>
                <dd className="text-right font-mono">{fmtPct(result.effectiveDiscount)}</dd>
              </dl>
            </CardContent>
          </Card>

          <Card data-testid="calc-break-even">
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Break-even benchmarks</CardTitle>
            </CardHeader>
            <CardContent className="text-sm">
              <dl className="grid grid-cols-2 gap-x-4 gap-y-2">
                <dt className="text-slate-600">Break-even basket (incl VAT)</dt>
                <dd className="text-right font-mono">
                  {isFinite(result.breakEvenBasketInclVat) ? fmt(result.breakEvenBasketInclVat) : "N/A"}
                </dd>
                <dt className="text-slate-600 col-span-2 text-xs italic pb-2">
                  Smallest basket that fully covers the voucher cost at the current margin.
                </dt>

                <dt className="text-slate-600">Break-even margin @ threshold</dt>
                <dd className="text-right font-mono">{fmtPct(result.breakEvenMarginPct)}</dd>
                <dt className="text-slate-600 col-span-2 text-xs italic pb-2">
                  Minimum GP % on the R{num(threshold).toFixed(0)} basket to not lose money.
                </dt>

                <dt className="text-slate-600">Uplift over baseline</dt>
                <dd className="text-right font-mono">{fmt(result.uplift)}</dd>

                <dt className="text-slate-600">Extra GP from that uplift</dt>
                <dd className="text-right font-mono">{fmt(result.upliftGp)}</dd>

                <dt className="text-slate-600">Equivalent flat discount</dt>
                <dd className="text-right font-mono">{fmtPct(result.equivalentDiscountPct)}</dd>
              </dl>
            </CardContent>
          </Card>

          <Alert className="border-slate-200 bg-slate-50">
            <Info className="h-4 w-4 text-slate-500" />
            <AlertDescription className="text-xs text-slate-700">
              <strong>Rule of thumb:</strong> the promo is a genuine winner
              when <em>Net profit vs baseline</em> is positive — i.e. the
              extra spend the promo drags into the shop is worth more in
              gross profit than the voucher costs you. If it&apos;s marginal,
              consider raising the threshold or lowering the voucher value.
            </AlertDescription>
          </Alert>
        </div>
      </div>
    </div>
  );
};

export default VoucherGiveawayCalculator;
